/**
 * @retail-ops/shared-types/admin
 *
 * Wave 6.1 ENTRA-SYNC-LIVE — wire shapes for the typed `/v3/admin/users/*`,
 * `/v3/admin/roles/*`, and `/v3/admin/user-role-assignments/*` write routes
 * that replace the legacy `runSeedSql` save path in usersEditor + rolesEditor.
 *
 * Conventions:
 *   • Frontend rows pass `userId: 0` for new users; the server resolves on Email.
 *   • `bypassKey` is forwarded as the `x-config-seed-bypass` HEADER, never in body.
 *   • Soft-delete is the only delete path — IsActive=0 on the row, audit preserved.
 */

// ─── User upsert ────────────────────────────────────────────────────────────

export interface AdminUserUpsertBody {
  /** 0 / undefined for new users; otherwise the existing UserId. */
  userId?: number;
  /** Lower-cased on the server — natural key. */
  email: string;
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  officerCode?: string | null;
  branchCode?: string | null;
  regionCode?: string | null;
  isOfficer?: boolean;
  isCompliance?: boolean;
  isActive?: boolean;
  /** Replace-all set of role codes (delete-old/insert-new under one save). */
  roleCodes?: ReadonlyArray<string>;
  /** Replace-all set of supervisor UserIds (subordinate=this user). */
  supervisorUserIds?: ReadonlyArray<number>;

  // ── Cart-mode rulebook per-user flags (Bill 2026-05-08; memo
  // project_dev_mode_sandbox_access_model_20260507.md). Server-side
  // gating: HasTestLiveAccess is SuperAdmin-only edit (silently dropped
  // for non-SuperAdmin callers); the other 4 are Admin-or-SuperAdmin.
  /** Forcing flag — when true, every cart this user opens runs in Sandbox. */
  isInSandboxMode?: boolean;
  /** Forcing flag — when true, eligible carts default to UAT-Live (Pilot). */
  isUATEnrolled?: boolean;
  /** Capability flag — when true, picker offers Test-Live opt-in. SuperAdmin-only edit. */
  hasTestLiveAccess?: boolean;
  /** Per-officer test inbox for Test-Live + Sandbox outbound suppression. */
  testRecipientEmail?: string | null;
  /** Per-officer test phone for Test-Live + Sandbox SMS suppression. */
  testRecipientPhone?: string | null;
  /**
   * TEST MODE (Bill 2026-10-07) — live Core Director, test identities only.
   * Mutually exclusive with isInSandboxMode (server refuses both on: MODE_CONFLICT).
   */
  isTestIdentitiesOnly?: boolean;
}

export interface AdminUserUpsertResultData {
  userId: number;
  created: boolean;
}

// ─── Role upsert ────────────────────────────────────────────────────────────

export interface AdminRoleUpsertBody {
  code: string;
  name: string;
  description?: string | null;
  effectiveLevel?: number;
  /** Mapped to entity column IsOrthogonal — built-in roles cannot be removed. */
  isSystem?: boolean;
  isActive?: boolean;
  sortOrder?: number | null;
}

// ─── User-role assignment ───────────────────────────────────────────────────

export interface AdminUserRoleAssignmentBody {
  userId: number;
  roleCode: string;
}

// ─── Entra sync ─────────────────────────────────────────────────────────────

/**
 * Optional body for POST /v3/admin/users/sync-from-entra. All fields optional —
 * server falls back to `PCB_ENTRA_SYNC_GROUP_ID` env var when no group is supplied.
 */
export interface AdminEntraSyncBody {
  /** Entra security-group object id. Members (transitive) are synced. */
  groupObjectId?: string;
  /** Optional case-insensitive department equality filter (applied in-process). */
  departmentFilter?: string;
  /**
   * When true (default), local users not present in Entra response are flipped
   * to IsActive=0. Set to false for partial syncs (e.g. one-group test).
   */
  deactivateMissing?: boolean;
}

export interface AdminEntraSyncResult {
  success: boolean;
  added: number;
  updated: number;
  deactivated: number;
  totalScanned: number;
  /** Object id of the Entra group filtered (when used). */
  groupFilter?: string;
  elapsedMs: number;
  error?: string;
  /**
   * Friendly error code for the editor toast:
   *   GRAPH_PERMISSION_DENIED — User.Read.All not granted (admin consent missing)
   *   GRAPH_UNAUTHORIZED      — Function MI not enabled / token rejected
   *   GRAPH_TOKEN_FAILED      — DefaultAzureCredential could not acquire a token
   *   GRAPH_FAILED            — generic Graph HTTP failure
   *   BYPASS_KEY_INVALID      — x-config-seed-bypass header rejected
   *   BYPASS_KEY_MISSING      — server-side bypass key not configured
   */
  code?: string;
}

// ─── Generic admin write envelope ───────────────────────────────────────────

export interface AdminWriteEnvelope<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// PILOT-GATE-INFRA (Bill 2026-04-25 binding pilot-gating rule)
//
// Two-layer gate: Layer 1 = SystemSettings.pilot.mode toggle, Layer 2 =
// explicit (OfficerCode × FeatureCode) opt-in rows. PilotOfficers powers the
// Layer-2 row. See server-side `Services/PilotGateService.cs` for the
// gate-check + the `// PILOT-GATE: <feature> — <message>` comment-marker
// convention every consumer must follow.
//
// Routes:
//   GET    /v3/admin/pilot-officers
//   POST   /v3/admin/pilot-officers/upsert
//   DELETE /v3/admin/pilot-officers/{officerCode}/{featureCode}
//   POST   /v3/admin/pilot-officers/bulk-enroll
//
// All write routes require the `x-config-seed-bypass: <bypassKey>` header
// (Wave-6.1 pattern; replaced by role-claim auth in the Wave-13
// SUPER-ADMIN-ROLE pass).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Closed list of pilot feature codes. Mirror of the editor matrix columns
 * AND the server-side `AllowedFeatureCodes` HashSet — keep them in lock-step
 * so a typo in one place can never silently introduce an un-gated feature.
 */
export type PilotFeatureCode =
  | 'weekly-tasks'
  | 'retail-qc'
  | 'fraud-cases'
  | 'anniversary-nurture'
  | 'cross-sell'
  | 'engagement-timeline';

export const PILOT_FEATURE_CODES: ReadonlyArray<PilotFeatureCode> = [
  'weekly-tasks',
  'retail-qc',
  'fraud-cases',
  'anniversary-nurture',
  'cross-sell',
  'engagement-timeline',
];

export interface PilotOfficer {
  officerCode: string;
  featureCode: PilotFeatureCode;
  isActive: boolean;
  enrolledAt?: string;
  enrolledBy?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string | null;
}

export interface PilotOfficerUpsertInput {
  officerCode: string;
  featureCode: PilotFeatureCode;
  isActive: boolean;
  notes?: string | null;
}

/**
 * Two mutually-exclusive variants — supply either `officerCodes` (many
 * officers, one feature) or `featureCodes` (one officer, many features).
 * The server returns 400 if neither variant is satisfied.
 */
export type PilotOfficerBulkEnrollInput =
  | {
      officerCodes: ReadonlyArray<string>;
      featureCode: PilotFeatureCode;
      notes?: string | null;
    }
  | {
      officerCode: string;
      featureCodes: ReadonlyArray<PilotFeatureCode>;
      notes?: string | null;
    };

export interface PilotOfficerBulkEnrollResultData {
  /** Brand-new rows inserted. */
  created: number;
  /** Existing rows whose IsActive flipped from false → true. */
  updated: number;
  /** `created + updated + (already-active rows untouched) = totalPairs`. */
  totalPairs: number;
}

// ── LexisNexis KBA + InstantID-search scaffold (Bill 2026-05-10 v0.14.17) ───
// Wire shapes for the scaffold endpoints in LexisNexisFunction.cs. The
// scaffold defaults to DISABLED at v3.0 launch; KBA endpoints return 503
// with errorCode LEXISNEXIS_DISABLED until admin flips
// `lexisnexis.enabled=true` in SystemSettings.

export interface LexisNexisEnabledOutcome {
  enabled: boolean;
  reason: string;
  environment?: string | null;
}

export interface LexisNexisKbaInput {
  cartId: string;
  signerCifNumber?: string | null;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  /** Raw SSN — server hashes before any log row is written. */
  ssn?: string | null;
  /** YYYY-MM-DD — server masks to year-only before logging. */
  dateOfBirth?: string | null;
  addressStreet1?: string | null;
  addressStreet2?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  /** Caller-supplied idempotency key. */
  callId?: string | null;
}

export interface LexisNexisKbaQuestion {
  questionId: string;
  prompt: string;
  choices: ReadonlyArray<string>;
}

export interface LexisNexisKbaOutcome {
  success: boolean;
  transactionId?: string | null;
  vendorTransactionId?: string | null;
  questions: ReadonlyArray<LexisNexisKbaQuestion>;
  expiresInSeconds: number;
  error?: string | null;
  errorCode?: string | null;
}

export interface LexisNexisKbaAnswer {
  questionId: string;
  choice: string;
}

export interface LexisNexisKbaVerifyInput {
  cartId: string;
  signerCifNumber?: string | null;
  transactionId: string;
  answers: ReadonlyArray<LexisNexisKbaAnswer>;
  callId?: string | null;
}

export interface LexisNexisKbaVerifyOutcome {
  success: boolean;
  pass: boolean;
  scoreCorrect: number;
  scoreTotal: number;
  vendorTransactionId?: string | null;
  error?: string | null;
  errorCode?: string | null;
}

export interface LexisNexisInstantIdInput {
  cartId: string;
  signerCifNumber?: string | null;
  firstName: string;
  lastName: string;
  ssn?: string | null;
  dateOfBirth?: string | null;
  addressStreet1?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  callId?: string | null;
}

export interface LexisNexisInstantIdOutcome {
  success: boolean;
  verificationScore?: number | null;
  verificationStatus?: string | null;
  reasonCodes: ReadonlyArray<string>;
  vendorTransactionId?: string | null;
  error?: string | null;
  errorCode?: string | null;
}

export interface LexisNexisCallLogFilter {
  fromUtc?: string | null;
  toUtc?: string | null;
  cartId?: string | null;
  product?: string | null;
  cifId?: string | null;
  top?: number;
}

export interface LexisNexisCallLogRow {
  callId: string;
  timestamp: string;
  product: string;
  cartMode: string;
  cartId?: string | null;
  cifId?: string | null;
  success: boolean;
  httpStatus: number;
  latencyMs: number;
  score?: number | null;
  reasonCodesJson?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  operatorCode?: string | null;
  sandboxOrProd?: string | null;
  conversationId?: string | null;
  requestHash: string;
  requestPiiHash?: string | null;
}

/**
 * Single-form config-panel payload edited by lexisNexisConfigEditor.tsx.
 * Each field maps 1:1 onto a SystemSettings row (key = the field name with
 * a `lexisnexis.` prefix in dotted form).
 */
export interface LexisNexisConfigUpsertBody {
  enabled: boolean;
  apiBaseUrl?: string | null;
  apiUsername?: string | null;
  apiPassword?: string | null;
  apiProduct?: string | null;
  apiEnvironment?: string | null; // 'production' | 'staging'
  budgetMonthlyMaxDollars?: number | null;
  retentionCallLogMonths?: number | null;
}

